import { useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { observer } from 'mobx-react-lite';
import { observable, runInAction } from 'mobx';
import { supportsSessionResume, type Identity } from '@cef-ai/wallet-identity';
import { createPopupBridge, createIframeBridge } from '~/bootstrap';
import { PendingConsentStore, PendingRegistrationStore } from '~/stores';
import { useWallet } from '~/wallet';
import { ConsentPrompt } from '~/popup/components/ConsentPrompt';
import { RegisterPrompt } from '~/popup/components/RegisterPrompt';
import { LogoLockup } from '~/ui/Logo';
import { Button, Spinner } from '~/ui/primitives';
import { createGatedIdentity } from './gatedIdentity';

/**
 * Popup/iframe-side route that receives postMessages from the host page and
 * routes them to identity/api-client/stores via `createPopupBridge` or
 * `createIframeBridge`. Spec §6.2.
 *
 * Identity + stores come from the surrounding `WalletProvider` (via
 * `useWallet`) — the route does NOT construct its own. A second identity tree
 * would mean two `BroadcastChannel` handles and two cross-tab subscribers in
 * the same window, with the provider's tree sitting unused. The provider
 * owns the identity lifecycle (it disposes on unmount), so this route only
 * owns the bridge + its consent store.
 *
 * Cycle 7: also renders `<ConsentPrompt>` whenever the `PendingConsentStore`
 * has an in-flight delegation prompt (Spec §3).
 *
 * Task 22: and `<RegisterPrompt>` whenever the popup bridge's
 * `PendingRegistrationStore` has an in-flight registration. S0 requires a
 * verified mailbox before the ceremony and only the wallet origin can mint the
 * token that proves it, so `wallet:hello{intent:'register'}` now runs email →
 * OTP → passkey HERE instead of failing by name. Popup-only, permanently:
 * Safari blocks WebAuthn `create()` in a cross-origin iframe, so registration
 * never moves into the framed surface.
 *
 * Task 7: when this route is loaded inside a cross-origin iframe (the
 * `IframeTransport` default — Task 6), `window.parent !== window.self` and we
 * wire `createIframeBridge` instead of `createPopupBridge`. The iframe bridge
 * replies via `window.parent` (Task 3's `replyTo: 'parent'`) and emits
 * `wallet:ui:show`/`wallet:ui:hide` while a consent prompt is pending, so the
 * host's `IframeTransport` overlay appears at the right times.
 *
 * Login gesture (framed only): a WebAuthn `get()` ceremony requires user
 * activation. Inside a cross-origin iframe, the `wallet:hello` message from
 * the host arrives asynchronously (postMessage), so calling `identity.login()`
 * directly from the handler has no activation to spend. We wrap `identity`
 * (via `createGatedIdentity`, see `./gatedIdentity.ts`) for the iframe bridge
 * so `login()` first waits for a "Continue" button click before running the
 * real ceremony. `register()` is unaffected: `EmbedWallet.register()` always
 * punches out to a real popup window (Task 6), so its `wallet:hello` never
 * reaches this iframe-gated path.
 *
 * Overlay visibility (framed only) has a SINGLE source of truth:
 * `createIframeBridge`'s internal reaction, which watches
 * `pendingConsentStore.pending !== null || loginGesturePending.get()` and is
 * the only code that ever sends `wallet:ui:show`/`wallet:ui:hide`. This route
 * owns `loginGesturePending` (an observable box) and only flips it — it does
 * NOT send `wallet:ui:*` itself. That avoids a race where this route's own
 * send and the bridge's consent-driven send could interleave and hide the
 * host overlay while the other condition is still true.
 */
export const EmbedWalletRoute = observer(() => {
  const [params] = useSearchParams();
  const origin = params.get('origin');
  const { identity, apiClient, appContextStore, applicationsStore, delegationsStore } = useWallet();
  const [status, setStatus] = useState<string>('Initializing...');
  const [pendingConsentStore, setPendingConsentStore] = useState<PendingConsentStore | null>(null);
  /**
   * Task 22. Set only on the POPUP branch below. `wallet:hello{intent:'register'}`
   * needs two screens of human input before its ceremony (S0 proves the mailbox
   * first), and `createHelloHandler` has nothing to render — so it awaits this
   * store and `<RegisterPrompt>` resolves it. Null while framed: WebAuthn
   * `create()` is blocked in a cross-origin iframe, so `EmbedWallet.register()`
   * always punches out to a popup and this prompt has no reason to exist there
   * (`createHelloHandler` says so by name if one ever arrives).
   */
  const [pendingRegistrationStore, setPendingRegistrationStore] = useState<PendingRegistrationStore | null>(null);
  const [awaitingLoginGesture, setAwaitingLoginGesture] = useState(false);
  const continueLoginRef = useRef<(() => void) | null>(null);
  // Task 3 / task #17: guards the boot-time silent-resume attempt to at most
  // one `session.refresh()` per IDENTITY INSTANCE.
  //
  // Holds the identity the attempt was made for, not a boolean. That is
  // load-bearing — see the effect below for why a boolean that cleanup reset
  // caused two concurrent refreshes on every StrictMode boot, and why two
  // concurrent refreshes make wallet-api burn the whole refresh-token family.
  // Keying on the instance also keeps the "one per identity" contract correct
  // across a genuine remount, without needing cleanup to reset anything.
  const resumeAttemptedForRef = useRef<Identity | null>(null);
  // Issue #36. The boot-resume attempt, hoisted OUT of its effect as a promise
  // so the bridge (created in the effect above this one) can hand it to
  // `createSessionStatusHandler`, which awaits it before reading
  // `identity.isAuthenticated`. Without this the iframe answers the host's
  // boot-time `wallet:session:status` while `session.refresh()` is still in
  // flight — truthfully `authenticated: false` — and the host's resume-first
  // `login()` falls through to a passkey popup on every page refresh.
  //
  // Built during RENDER, not in an effect, because effects run in declaration
  // order: the bridge effect is first, so an effect-created promise would still
  // be undefined when `createIframeBridge` needs it.
  //
  // A deferred (promise + `settle`) rather than starting the network call here:
  // the `session.refresh()` call itself stays inside the effect below (render
  // must stay side-effect free), and the effect resolves this handle when it
  // settles. Lazily initialised through a ref rather than `useMemo` so React 18
  // `<StrictMode>`'s double render (always on — see `src/main.tsx`) reuses ONE
  // deferred: refs are per component instance, shared across both render
  // passes, whereas `useMemo` is not a semantic guarantee.
  const bootResumeRef = useRef<{ promise: Promise<void>; settle: () => void } | null>(null);
  if (!bootResumeRef.current) {
    // The executor runs synchronously, so `settle` is always assigned before
    // anything can read it.
    let settle!: () => void;
    const promise = new Promise<void>((resolve) => {
      settle = resolve;
    });
    bootResumeRef.current = { promise, settle };
  }
  const bootResume = bootResumeRef.current;

  useEffect(() => {
    if (!origin) {
      setStatus('Error: missing ?origin= in URL');
      return;
    }
    const framed = window.parent !== window.self;
    let bridge: ReturnType<typeof createPopupBridge>['bridge'];
    let pcs: PendingConsentStore;
    let prs: PendingRegistrationStore | null = null;
    let disposeBridgeWiring: () => void;

    if (framed) {
      // Overlay visibility for the framed path has exactly one emitter:
      // createIframeBridge's internal reaction (see that file). This box is
      // the route's half of that combined expression — flipped true while
      // the "Continue" button is showing, false once it's clicked — and is
      // never used to send `wallet:ui:*` directly.
      const loginGesturePending = observable.box(false);
      // Gate `login()` behind a "Continue" click so the WebAuthn `get()`
      // ceremony it triggers runs inside a real user-activation event.
      // `createGatedIdentity` delegates every other member straight through
      // to the real `identity` (including popup-only members such as
      // `IdentityImpl.getVault`, which `createSignHandler` requires).
      const gatedIdentity = createGatedIdentity(identity, {
        onAwaitGesture: (proceed) => {
          runInAction(() => loginGesturePending.set(true));
          setAwaitingLoginGesture(true);
          continueLoginRef.current = () => {
            runInAction(() => loginGesturePending.set(false));
            setAwaitingLoginGesture(false);
            continueLoginRef.current = null;
            proceed();
          };
        },
      });
      const iframeBridge = createIframeBridge({
        hostOrigin: origin,
        popupWindow: window,
        identity: gatedIdentity,
        apiClient,
        appContextStore,
        applicationsStore,
        delegationsStore,
        loginGesturePending,
        // Issue #36: gate `wallet:session:status` on the boot resume below.
        bootResume: bootResume.promise,
      });
      bridge = iframeBridge.bridge;
      pcs = iframeBridge.pendingConsentStore;
      disposeBridgeWiring = iframeBridge.dispose;
    } else {
      const popupBridge = createPopupBridge({
        hostOrigin: origin,
        popupWindow: window,
        identity,
        apiClient,
        appContextStore,
        applicationsStore,
        delegationsStore,
        // Issue #36: same gate on the popup surface — see createPopupBridge.
        bootResume: bootResume.promise,
      });
      bridge = popupBridge.bridge;
      pcs = popupBridge.pendingConsentStore;
      prs = popupBridge.pendingRegistrationStore;
      disposeBridgeWiring = popupBridge.dispose;
    }
    // `announceReady` posts a `wallet:ready` envelope to the opener/parent AFTER
    // the bridge's `message` listener is attached. The host-side transport
    // queues all `HostToPopup` messages until it sees this — without it the
    // first message races against the document's load and gets dropped.
    bridge.start({ announceReady: { id: 'embed-wallet-ready' } });
    setPendingConsentStore(pcs);
    setPendingRegistrationStore(prs);
    setStatus(`Waiting for host (${origin})`);
    // On unmount: stop the bridge and release the auxiliary reactions
    // registered by createPopupBridge/createIframeBridge. Identity disposal
    // belongs to WalletProvider, which owns the identity instance.
    return () => {
      bridge.stop();
      // `dispose()` also settles any in-flight registration prompt as
      // cancelled (see `createPopupBridge`), so the awaiting hello handler
      // cannot be left holding a promise nothing can resolve.
      disposeBridgeWiring();
      setAwaitingLoginGesture(false);
      continueLoginRef.current = null;
    };
  }, [origin, identity, apiClient, appContextStore, applicationsStore, delegationsStore]);

  // Task 3: silent session resume on boot. When this route mounts and the
  // identity is not already authenticated, attempt `apiClient.session.refresh()`
  // exactly once. On success the response is threaded straight into
  // `identity.resumeSession()`, which puts the identity into "bearer-active"
  // state (Task 2: isAuthenticated=true, hasKeys()=false, no vault entry) with
  // NO WebAuthn ceremony. On a 401 (`WalletError` with code `refresh-invalid`
  // or `unauthorized`) — or any other refresh failure — this swallows the
  // error and leaves the identity logged out; the host is expected to call
  // `login()` later, which does prompt. This is what makes a reload or a
  // fresh tab silent: the SPA re-establishes its session from the wallet-origin
  // refresh cookie before the host ever asks it to do anything.
  //
  // Guarded by `resumeAttemptedForRef` (exactly one `session.refresh()` per
  // identity instance) and by `identity.isAuthenticated` (never clobber an
  // already-authenticated session — e.g. one adopted via the
  // BroadcastSessionShare handoff effect below, or a standalone tab that already
  // has live signing keys). `supportsSessionResume` mirrors
  // `supportsSessionHandoff`: thin test doubles that don't implement the
  // bearer-resume capability are skipped rather than crashing.
  //
  // ---- Why this is single-flight, and why that is a SERVER-SAFETY concern ----
  //
  // Task #17. React 18 `<StrictMode>` (always on — see `src/main.tsx`; dev-only,
  // compiled away by `vite build`, but very much live under `vite`/`pnpm dev`,
  // which is what the Playwright e2e webServer spawns) double-invokes effects on
  // mount: effect → cleanup → effect, on the SAME component instance.
  //
  // This effect previously used a boolean ref that its cleanup RESET, so both
  // invocations issued a real `session.refresh()`. That was a deliberate fix for
  // a real bug — with a permanently-true boolean, invocation 1 claimed the
  // attempt, StrictMode ran its cleanup immediately (setting that closure's
  // `cancelled = true` before the fetch resolved), invocation 2 declined to
  // retry, and a perfectly successful refresh was silently dropped, leaving the
  // identity logged out. Resetting the ref made the surviving invocation fetch.
  //
  // But TWO CONCURRENT REFRESHES ARE NOT SAFE. wallet-api's `session.Refresh()`
  // burns the ENTIRE refresh-token family when two refreshes race: its
  // `MarkRotated` compare-and-set miss cannot distinguish a concurrent
  // legitimate refresh from replay of a stolen token, so it treats it as theft.
  // The user is silently logged out and forced through a fresh passkey ceremony
  // — and under the Bearer-delegation model that also kills data-plane reads.
  // So the old shape made every dev boot roll the dice on burning the session,
  // which is very likely what has presented as "resume is flaky locally".
  //
  // The fix keys the attempt to the identity INSTANCE and lets the single
  // in-flight attempt's result land, rather than cancelling it. Both goals are
  // then satisfied at once: one refresh, and its result applied.
  //
  // Two details that must not be "simplified" away:
  //
  //   1. The already-attempted branch returns WITHOUT settling `bootResume`.
  //      The in-flight attempt owns settling it. If the second StrictMode
  //      invocation settled the gate on its way out, it would open before the
  //      refresh resolved and reintroduce the issue-#36 race that gate exists to
  //      close — while every unit test still passed.
  //   2. There is no cleanup, and no `cancelled` flag. Cancelling is what
  //      dropped the successful refresh in the first place. Applying the result
  //      after an unmount is harmless: `WalletProvider` owns identity disposal,
  //      so the identity is still live, and `resumeSession` only sets in-memory
  //      session state.
  //
  // Trade-off, stated: a remount with the SAME identity whose earlier refresh
  // failed (e.g. a 401) will not retry. That is intentional — a retry would
  // 401 again for the same reason, and re-attempting per mount is how a refresh
  // storm starts. A genuinely new identity instance does get its own attempt.
  useEffect(() => {
    // Already attempted for THIS identity — in flight or settled. Return
    // without touching `bootResume`: the in-flight attempt settles it (see
    // detail 1 above).
    if (resumeAttemptedForRef.current === identity) return;
    // Nothing to attempt: no session to resume, or a double without the
    // capability. That IS a settled outcome, so release the gate — a host
    // asking for session status must not wait on a resume that will never run.
    if (identity.isAuthenticated || !supportsSessionResume(identity)) {
      bootResume.settle();
      return;
    }
    resumeAttemptedForRef.current = identity;
    apiClient.session
      .refresh()
      .then((session) => {
        if (identity.isAuthenticated) return;
        // `SessionResponse.addresses` is the wire-format `Addresses` (shared
        // with register/login responses, where the server's echo is only a
        // soft cross-check against client-derived values — see
        // `IdentityImpl.crossCheckAddresses`). `resumeSession` has no
        // client-derived addresses to fall back on: the refresh response IS
        // the only source. Treat an incomplete response as a failed resume
        // (fail closed) rather than widening `SessionResume`'s contract to
        // accept partial addresses.
        const { cere, solana, evm } = session.addresses;
        if (!cere || !solana || !evm) return;
        identity.resumeSession({ ...session, addresses: { cere, solana, evm } });
      })
      .catch(() => {
        // Swallow cleanly — no live refresh cookie, an expired one, or a
        // network error all resolve to "stay logged out". The host calling
        // login() later is the only path back in; no error is surfaced here.
      })
      .finally(() => {
        // Issue #36. Release the session-status gate now that the resume has
        // settled either way. This is the ONLY place that settles it on the
        // attempting path — see detail 1 above.
        bootResume.settle();
      });
  }, [identity, apiClient, bootResume]);

  // S1 PR2 deleted the BroadcastSessionShare requester and its adopt effect.
  // The frame no longer receives a session from a sibling surface at all: after
  // a registration popup the frame re-derives its own key from PRF, which is
  // what makes "keys never cross a surface boundary" structural rather than
  // merely observed.

  const isError = status.startsWith('Error');
  return (
    <>
      <main
        style={{
          minHeight: '100vh',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 'var(--space-4)',
          padding: 'var(--space-6)',
          textAlign: 'center',
          background: 'var(--bg-default)',
        }}
      >
        <LogoLockup height={28} />
        {awaitingLoginGesture ? (
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 'var(--space-3)' }}>
            <p style={{ margin: 0, font: '400 14px/1.5 var(--font-sans)', color: 'var(--text-secondary)' }}>
              Continue to sign in with your passkey.
            </p>
            <Button fullWidth={false} onClick={() => continueLoginRef.current?.()} aria-label="Continue signing in">
              Continue
            </Button>
          </div>
        ) : (
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 'var(--space-2)',
              color: isError ? 'var(--c-red)' : 'var(--text-secondary)',
            }}
          >
            {!isError && <Spinner size={16} color="var(--c-violet)" />}
            <p style={{ margin: 0, font: '400 14px/1.5 var(--font-sans)' }}>{status}</p>
          </div>
        )}
      </main>
      {pendingConsentStore && <ConsentPrompt store={pendingConsentStore} />}
      {pendingRegistrationStore && <RegisterPrompt store={pendingRegistrationStore} />}
    </>
  );
});
